"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import DecryptedText from "./DecryptedText";

// Types for mempool data
interface PendingBlock {
  index: number;
  txCount: number;
  size: number;
  totalFees: number;
  medianFee: number;
  estimatedTime: number;
}

interface ConfirmedBlock {
  height: number;
  timestamp: number;
  txCount: number;
  size: number;
  totalFees: number;
  medianFee: number;
}

interface MempoolResponse {
  mempool: {
    count: number;
    vsize: number;
    totalFee: number;
  };
  pendingBlocks: PendingBlock[];
  confirmedBlocks: ConfirmedBlock[];
  tipHeight: number;
  lastUpdate: number;
}

// Format sat/vB to readable string
function formatFeeRate(satPerVb: number): string {
  if (satPerVb < 1) return "<1";
  return Math.round(satPerVb).toString();
}

// Format BTC amount (max 3 decimal places to prevent line breaks)
function formatBtc(satoshis: number): string {
  const btc = satoshis / 100000000;
  if (btc >= 1) return btc.toFixed(2);
  return btc.toFixed(3);
}

// Format transaction count
function formatTxCount(count: number): string {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toString();
}

// Get color for pending blocks (blue-tinted, varies by fee)
function getPendingBlockColor(medianFee: number): string {
  if (medianFee >= 100) return "rgba(59, 130, 246, 0.4)";
  if (medianFee >= 50) return "rgba(59, 130, 246, 0.35)";
  if (medianFee >= 20) return "rgba(59, 130, 246, 0.3)";
  if (medianFee >= 10) return "rgba(59, 130, 246, 0.25)";
  if (medianFee >= 5) return "rgba(59, 130, 246, 0.2)";
  return "rgba(59, 130, 246, 0.15)";
}

// Get color for confirmed blocks (orange-tinted, varies by fee)
function getConfirmedBlockColor(medianFee: number): string {
  if (medianFee >= 100) return "rgba(247, 147, 26, 0.4)";
  if (medianFee >= 50) return "rgba(247, 147, 26, 0.35)";
  if (medianFee >= 20) return "rgba(247, 147, 26, 0.3)";
  if (medianFee >= 10) return "rgba(247, 147, 26, 0.25)";
  if (medianFee >= 5) return "rgba(247, 147, 26, 0.2)";
  return "rgba(247, 147, 26, 0.15)";
}

// Interpolate between blue and RED colors (for transitioning block during movement)
// Blue: rgb(59, 130, 246) → Red: rgb(239, 68, 68)
function interpolateColor(progress: number): {
  bg: string;
  border: string;
  accent: string;
} {
  const r = Math.round(59 + (239 - 59) * progress);
  const g = Math.round(130 + (68 - 130) * progress);
  const b = Math.round(246 + (68 - 246) * progress);
  const opacity = 0.2 + progress * 0.05;

  return {
    bg: `rgba(${r}, ${g}, ${b}, ${opacity})`,
    border: `rgba(${r}, ${g}, ${b}, 0.4)`,
    accent: `rgb(${r}, ${g}, ${b})`,
  };
}

// Matrix "red pill" effect: interpolate from red to orange
// Red = truth revealed, Orange = Bitcoin/confirmed state
// Red: rgb(239, 68, 68) → Orange: rgb(247, 147, 26)
function getRedPillColor(progress: number): { accent: string; glow: string } {
  const r = Math.round(239 + (247 - 239) * progress);
  const g = Math.round(68 + (147 - 68) * progress);
  const b = Math.round(68 + (26 - 68) * progress);

  return {
    accent: `rgb(${r}, ${g}, ${b})`,
    glow: `rgba(${r}, ${g}, ${b}, 0.6)`,
  };
}

// Get confirmed block color with red pill effect during animation
function getConfirmedBlockColorAnimated(
  medianFee: number,
  animationProgress: number
): {
  bg: string;
  border: string;
} {
  // Interpolate from red to orange based on animation progress
  const r = Math.round(239 + (247 - 239) * animationProgress);
  const g = Math.round(68 + (147 - 68) * animationProgress);
  const b = Math.round(68 + (26 - 68) * animationProgress);

  // Opacity based on fee (same logic as original)
  let opacity = 0.15;
  if (medianFee >= 100) opacity = 0.4;
  else if (medianFee >= 50) opacity = 0.35;
  else if (medianFee >= 20) opacity = 0.3;
  else if (medianFee >= 10) opacity = 0.25;
  else if (medianFee >= 5) opacity = 0.2;

  return {
    bg: `rgba(${r}, ${g}, ${b}, ${opacity})`,
    border: `rgba(${r}, ${g}, ${b}, 0.3)`,
  };
}

// Layout constants
const BLOCK_SIZE = 130;
const BLOCK_GAP = 8;
const DIVIDER_WIDTH = 3;
const DIVIDER_MARGIN = 12;
const SLOT_WIDTH = BLOCK_SIZE + BLOCK_GAP;

// Calculate X position for a slot index
// Slots 0-3: pending (left side)
// Slots 4-7: confirmed (right side)
function getSlotPosition(slotIndex: number, numPending: number): number {
  if (slotIndex < numPending) {
    // Pending side
    return slotIndex * SLOT_WIDTH;
  } else {
    // Confirmed side (after divider)
    const pendingWidth = numPending * SLOT_WIDTH - BLOCK_GAP; // Remove last gap
    const dividerSpace = DIVIDER_MARGIN + DIVIDER_WIDTH + DIVIDER_MARGIN;
    const confirmedIndex = slotIndex - numPending;
    return pendingWidth + dividerSpace + confirmedIndex * SLOT_WIDTH;
  }
}

interface BlockCardProps {
  isPending?: boolean;
  isTransitioning?: boolean;
  transitionProgress?: number;
  redPillProgress?: number; // For Matrix "red pill" effect on confirmed blocks
  height?: number;
  txCount: number;
  medianFee: number;
  totalFees: number;
  style?: React.CSSProperties;
}

function BlockCard({
  isPending = false,
  isTransitioning = false,
  transitionProgress = 0,
  redPillProgress,
  height,
  txCount,
  medianFee,
  totalFees,
  style,
}: BlockCardProps) {
  let bgColor: string;
  let borderColor: string;
  let accentColor: string;

  if (isTransitioning) {
    const colors = interpolateColor(transitionProgress);
    bgColor = colors.bg;
    borderColor = colors.border;
    accentColor = colors.accent;
  } else if (isPending) {
    bgColor = getPendingBlockColor(medianFee);
    borderColor = "rgba(59, 130, 246, 0.3)";
    accentColor = "#3B82F6";
  } else if (redPillProgress !== undefined) {
    // Matrix "red pill" effect: red → orange during animation
    const animatedColors = getConfirmedBlockColorAnimated(
      medianFee,
      redPillProgress
    );
    const redPillColors = getRedPillColor(redPillProgress);
    bgColor = animatedColors.bg;
    borderColor = animatedColors.border;
    accentColor = redPillColors.accent;
  } else {
    bgColor = getConfirmedBlockColor(medianFee);
    borderColor = "rgba(247, 147, 26, 0.3)";
    accentColor = "#F7931A";
  }

  const textColor = "#b7ab98";

  return (
    <div
      style={{
        position: "absolute",
        display: "flex",
        flexDirection: "column",
        padding: "12px",
        borderRadius: "8px",
        justifyContent: "center",
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        width: `${BLOCK_SIZE}px`,
        height: `${BLOCK_SIZE}px`,
        boxSizing: "border-box",
        ...style,
      }}
    >
      <div style={{ textAlign: "center" }}>
        {isPending && !isTransitioning ? (
          <p style={{ fontSize: "0.75rem", opacity: 0.7, color: textColor, margin: 0 }}>
            {formatFeeRate(medianFee)} sat/vB
          </p>
        ) : (
          <p style={{ fontWeight: 700, fontSize: "0.875rem", color: accentColor, margin: 0 }}>
            {height?.toLocaleString()}
          </p>
        )}
      </div>
      <div style={{ textAlign: "center", margin: "8px 0" }}>
        <p
          style={{
            fontWeight: 700,
            color: textColor,
            fontSize: "clamp(0.875rem, 2vw, 1.125rem)",
            margin: 0,
          }}
        >
          {formatBtc(totalFees)} BTC
        </p>
        <p style={{ fontSize: "0.75rem", opacity: 0.7, color: textColor, margin: 0 }}>
          {formatTxCount(txCount)} Transaktionen
        </p>
      </div>
    </div>
  );
}

interface BlockchainVisualizerProps {
  className?: string;
  showPendingBlocks?: number;
  showConfirmedBlocks?: number;
  refreshInterval?: number;
  showTestButton?: boolean;
}

interface TransitioningBlockData {
  height: number;
  txCount: number;
  medianFee: number;
  totalFees: number;
}

export function BlockchainVisualizer({
  className = "",
  showPendingBlocks = 4,
  showConfirmedBlocks = 4,
  refreshInterval = 30000,
  showTestButton = false,
}: BlockchainVisualizerProps) {
  const [data, setData] = useState<MempoolResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Animation state - Four phases:
  // Phase 1 (Pre): orange → red, text: Mempool → MATRIX
  // Phase 2 (Main): block moves, colors stay RED, text stays MATRIX/WAHRHEIT
  // Phase 3 (Hold + Reverse): colors stay RED, text: MATRIX → Mempool
  // Phase 4 (Post): red → orange, text already Mempool/Blockchain
  const prevTipHeightRef = useRef<number | null>(null);
  const [isPreAnimating, setIsPreAnimating] = useState(false); // Phase 1: orange → red
  const [preAnimationProgress, setPreAnimationProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false); // Phase 2: block transition (colors stay red)
  const [animationProgress, setAnimationProgress] = useState(0);
  const [isPostAnimating, setIsPostAnimating] = useState(false); // Phase 3: red → orange
  const [postAnimationProgress, setPostAnimationProgress] = useState(0);
  const [isHoldingText, setIsHoldingText] = useState(false); // Brief pause showing MATRIX/WAHRHEIT before reverse
  const [isReverseDecrypting, setIsReverseDecrypting] = useState(false); // Phase 4: Matrix → Mempool, Wahrheit → Blockchain
  const [transitioningBlock, setTransitioningBlock] =
    useState<TransitioningBlockData | null>(null);
  const animationRef = useRef<number | null>(null);
  const pendingBlockRef = useRef<TransitioningBlockData | null>(null); // Store block data for after pre-animation

  const numPending = showPendingBlocks;
  const numConfirmed = showConfirmedBlocks;
  const totalSlots = numPending + numConfirmed;

  // Calculate total width
  const pendingWidth = numPending * SLOT_WIDTH - BLOCK_GAP;
  const dividerSpace = DIVIDER_MARGIN + DIVIDER_WIDTH + DIVIDER_MARGIN;
  const confirmedWidth = numConfirmed * SLOT_WIDTH - BLOCK_GAP;
  const totalWidth = pendingWidth + dividerSpace + confirmedWidth;
  const dividerX = pendingWidth + DIVIDER_MARGIN;

  // Start animation manually (for testing Matrix "red pill" effect)
  // Phase 1: Pre-animation (orange → red, text encrypts to MATRIX/WAHRHEIT)
  // Phase 2: Block transition (colors stay red, text stays MATRIX/WAHRHEIT)
  // Phase 3: Hold + Reverse decryption (colors stay red, text: MATRIX → Mempool)
  // Phase 4: Post-animation (red → orange)
  const triggerTestAnimation = useCallback(() => {
    if (isPreAnimating || isAnimating || isPostAnimating || isHoldingText || isReverseDecrypting || !data) return;

    const pendingBlock = data.pendingBlocks[0];
    if (pendingBlock) {
      // Store block data for later (after pre-animation)
      pendingBlockRef.current = {
        height: (data.tipHeight || 0) + 1,
        txCount: pendingBlock.txCount,
        medianFee: pendingBlock.medianFee,
        totalFees: pendingBlock.totalFees,
      };
      // Start pre-animation (orange → red)
      setIsPreAnimating(true);
      setPreAnimationProgress(0);
    }
  }, [isPreAnimating, isAnimating, isPostAnimating, isHoldingText, isReverseDecrypting, data]);

  const fetchData = useCallback(async () => {
    try {
      // Lokale Entwicklung erkennen:
      // - localhost/127.0.0.1 = direkt auf dem Entwicklungsrechner
      // - Port 3333 = Frontend-Dev-Server (auch wenn über LAN-IP aufgerufen)
      // - 192.168.x.x / 10.x.x.x = lokales Netzwerk (Tablet-Test)
      const hostname = window.location.hostname;
      const port = window.location.port;
      const isLocalDev =
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        port === "3333" ||
        hostname.startsWith("192.168.") ||
        hostname.startsWith("10.");
      const baseUrl = isLocalDev ? `http://${hostname === "localhost" || hostname === "127.0.0.1" ? "localhost" : hostname}:5555` : "";

      const response = await fetch(`${baseUrl}/mempool`);
      if (!response.ok) throw new Error("Failed to fetch mempool data");

      const json: MempoolResponse = await response.json();

      if (
        prevTipHeightRef.current !== null &&
        json.tipHeight > prevTipHeightRef.current &&
        !isPreAnimating &&
        !isAnimating &&
        !isPostAnimating &&
        !isHoldingText &&
        !isReverseDecrypting
      ) {
        const newBlock = json.confirmedBlocks[0];
        if (newBlock) {
          // Store block data for after pre-animation
          pendingBlockRef.current = {
            height: newBlock.height,
            txCount: newBlock.txCount,
            medianFee: newBlock.medianFee,
            totalFees: newBlock.totalFees,
          };
          // Start pre-animation (orange → red)
          setIsPreAnimating(true);
          setPreAnimationProgress(0);
        }
      }

      prevTipHeightRef.current = json.tipHeight;
      setData(json);
      setError(null);
    } catch (err) {
      console.error("Error fetching mempool data:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [isPreAnimating, isAnimating, isPostAnimating, isHoldingText, isReverseDecrypting]);

  // Pre-animation loop: orange → red (Phase 1)
  useEffect(() => {
    if (!isPreAnimating) return;

    const duration = 400; // 400ms for orange → red fade
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smooth ease-out for fade to red
      const eased = 1 - Math.pow(1 - progress, 3);

      setPreAnimationProgress(eased);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Pre-animation complete - start main animation
        setIsPreAnimating(false);
        setPreAnimationProgress(0);

        // Transfer block data and start main animation
        if (pendingBlockRef.current) {
          setTransitioningBlock(pendingBlockRef.current);
          pendingBlockRef.current = null;
        }
        setIsAnimating(true);
        setAnimationProgress(0);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPreAnimating]);

  // Main animation loop: block transition - colors stay RED (Phase 2)
  useEffect(() => {
    if (!isAnimating) return;

    const duration = 1500;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing: ease-in-out cubic
      const eased =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      setAnimationProgress(eased);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Block movement complete - start holding phase (colors stay red)
        setIsAnimating(false);
        setTransitioningBlock(null);
        setAnimationProgress(0);
        // Start Hold Phase: keep MATRIX/WAHRHEIT visible with red colors
        setIsHoldingText(true);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimating]);

  // Hold phase: Brief pause showing MATRIX/WAHRHEIT with red colors before reverse decryption
  useEffect(() => {
    if (!isHoldingText) return;

    // Wait 800ms so the completed MATRIX/WAHRHEIT text is visible, then start reverse
    const timeout = setTimeout(() => {
      setIsHoldingText(false);
      setIsReverseDecrypting(true);
    }, 800);

    return () => clearTimeout(timeout);
  }, [isHoldingText]);

  // Post-animation loop: red → orange (Phase 4 - after reverse decryption)
  useEffect(() => {
    if (!isPostAnimating) return;

    const duration = 600; // 600ms for red → orange fade
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smooth ease-out for fade to orange
      const eased = 1 - Math.pow(1 - progress, 3);

      setPostAnimationProgress(eased);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Color animation complete - all done
        setIsPostAnimating(false);
        setPostAnimationProgress(0);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPostAnimating]);

  // Phase 3: Reverse decryption (Matrix → Mempool, Wahrheit → Blockchain) - colors still red
  useEffect(() => {
    if (!isReverseDecrypting) return;

    // Duration matches the decryption effect: ~2000ms (100ms * ~20 iterations for longer word)
    const timeout = setTimeout(() => {
      setIsReverseDecrypting(false);
      // Now start post-animation: red → orange
      setIsPostAnimating(true);
      setPostAnimationProgress(0);
    }, 2200);

    return () => clearTimeout(timeout);
  }, [isReverseDecrypting]);

  // Polling
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  // Loading skeleton
  if (loading && !data) {
    return (
      <div
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {Array.from({ length: showPendingBlocks }).map((_, i) => (
            <div
              key={`pending-${i}`}
              style={{
                width: `${BLOCK_SIZE}px`,
                height: `${BLOCK_SIZE}px`,
                backgroundColor: "rgba(59, 130, 246, 0.15)",
                border: "1px solid rgba(59, 130, 246, 0.2)",
                borderRadius: "8px",
                animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
              }}
            />
          ))}
        </div>
        <div
          style={{
            margin: "0 12px",
            width: `${DIVIDER_WIDTH}px`,
            height: "160px",
            backgroundColor: "rgba(247, 147, 26, 0.3)",
          }}
        />
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {Array.from({ length: showConfirmedBlocks }).map((_, i) => (
            <div
              key={`confirmed-${i}`}
              style={{
                width: `${BLOCK_SIZE}px`,
                height: `${BLOCK_SIZE}px`,
                backgroundColor: "rgba(247, 147, 26, 0.15)",
                border: "1px solid rgba(247, 147, 26, 0.2)",
                borderRadius: "8px",
                animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className={className} style={{ textAlign: "center" }}>
        <p style={{ color: "hsla(37, 18%, 66%, 0.5)", margin: 0 }}>
          Blockchain-Daten konnten nicht geladen werden
        </p>
      </div>
    );
  }

  if (!data) return null;

  // Get blocks for display (reversed for pending so index 0 is leftmost)
  const pendingBlocks = [...data.pendingBlocks.slice(0, numPending)].reverse();
  const confirmedBlocks = data.confirmedBlocks.slice(0, numConfirmed);

  // Helper: interpolate between two X positions
  const lerp = (start: number, end: number, t: number) =>
    start + (end - start) * t;

  // Get current "red pill" color for right side during animation
  // Phase 1 (Pre): orange → red
  // Phase 2 (Main): stay RED (block is moving)
  // Phase 3 (Hold + Reverse): stay RED (text decrypts back)
  // Phase 4 (Post): red → orange
  const getRightSideColor = (): string => {
    if (isPreAnimating) {
      // Phase 1: orange → red
      return getRedPillColor(1 - preAnimationProgress).accent;
    }
    if (isAnimating || isHoldingText || isReverseDecrypting) {
      // Phase 2 + 3: stay RED while block moves and text decrypts back
      return getRedPillColor(0).accent; // 0 = full red
    }
    if (isPostAnimating) {
      // Phase 4: red → orange
      return getRedPillColor(postAnimationProgress).accent;
    }
    return "#F7931A"; // Default orange
  };
  const rightSideColor = getRightSideColor();

  return (
    <div
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Labels - aligned with block containers below */}
      {/* During animation: "Mempool" → "Matrix" (glitch), "Blockchain" → "Wahrheit" (glitch) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          width: `${totalWidth}px`,
        }}
      >
        {/* Left label - "Mempool" or "Matrix" during animation */}
        <div style={{ textAlign: "center", width: `${pendingWidth}px` }}>
          <p
            style={{
              fontSize: "0.875rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#3B82F6",
              fontFamily: "'Avant Garde', sans-serif",
              margin: 0,
            }}
          >
            {isPreAnimating || isAnimating || isHoldingText ? (
              <DecryptedText
                text="MATRIX"
                speed={100}
                maxIterations={20}
                sequential={true}
                revealDirection="start"
                isActive={isPreAnimating || isAnimating}
                animateOn="hover"
              />
            ) : isReverseDecrypting ? (
              <DecryptedText
                text="MEMPOOL"
                speed={100}
                maxIterations={20}
                sequential={true}
                revealDirection="start"
                isActive={isReverseDecrypting}
                animateOn="hover"
              />
            ) : (
              "Mempool"
            )}
          </p>
        </div>
        {/* Divider space */}
        <div style={{ width: `${dividerSpace}px` }} />
        {/* Right label - "Blockchain" or "Wahrheit" during animation */}
        {/* Color transitions: red → orange with animation progress */}
        <div style={{ textAlign: "center", width: `${confirmedWidth}px` }}>
          <p
            style={{
              fontSize: "0.875rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: rightSideColor,
              fontFamily: "'Avant Garde', sans-serif",
              transition: "color 0.1s ease-out",
              margin: 0,
            }}
          >
            {isPreAnimating || isAnimating || isHoldingText ? (
              <DecryptedText
                text="WAHRHEIT"
                speed={100}
                maxIterations={20}
                sequential={true}
                revealDirection="start"
                isActive={isPreAnimating || isAnimating}
                animateOn="hover"
              />
            ) : isReverseDecrypting ? (
              <DecryptedText
                text="BLOCKCHAIN"
                speed={100}
                maxIterations={20}
                sequential={true}
                revealDirection="start"
                isActive={isReverseDecrypting}
                animateOn="hover"
              />
            ) : (
              "Blockchain"
            )}
          </p>
        </div>
      </div>

      {/* Blocks container - relative with fixed dimensions */}
      <div
        style={{
          position: "relative",
          width: `${totalWidth}px`,
          height: `${BLOCK_SIZE}px`,
        }}
      >
        {/* Pending blocks (slots 0 to numPending-1) */}
        {pendingBlocks.map((block, idx) => {
          // During animation:
          // - The rightmost pending block (idx === numPending-1) becomes the transitioning block
          // - All other pending blocks shift right by one slot
          // - A new block appears at slot 0 (from off-screen left)

          const isTransitioningSlot = isAnimating && idx === numPending - 1;
          if (isTransitioningSlot) {
            return null; // Rendered separately as transitioning block
          }

          // Calculate position
          let xPos: number;
          if (isAnimating) {
            // Shift from current slot to next slot
            const fromSlot = idx;
            const toSlot = idx + 1;
            const fromX = getSlotPosition(fromSlot, numPending);
            const toX = getSlotPosition(toSlot, numPending);
            xPos = lerp(fromX, toX, animationProgress);
          } else {
            xPos = getSlotPosition(idx, numPending);
          }

          return (
            <BlockCard
              key={`pending-${block.index}`}
              isPending
              txCount={block.txCount}
              medianFee={block.medianFee}
              totalFees={block.totalFees}
              style={{
                left: `${xPos}px`,
                top: 0,
              }}
            />
          );
        })}

        {/* New pending block sliding in from left during animation */}
        {isAnimating && pendingBlocks.length > 0 && (
          <BlockCard
            isPending
            txCount={pendingBlocks[0]?.txCount || 0}
            medianFee={pendingBlocks[0]?.medianFee || 1}
            totalFees={pendingBlocks[0]?.totalFees || 0}
            style={{
              left: `${lerp(-SLOT_WIDTH, 0, animationProgress)}px`,
              top: 0,
              opacity: animationProgress,
            }}
          />
        )}

        {/* Transitioning block - from last pending slot to first confirmed slot */}
        {isAnimating && transitioningBlock && (
          <BlockCard
            isTransitioning
            transitionProgress={animationProgress}
            height={transitioningBlock.height}
            txCount={transitioningBlock.txCount}
            medianFee={transitioningBlock.medianFee}
            totalFees={transitioningBlock.totalFees}
            style={{
              left: `${lerp(
                getSlotPosition(numPending - 1, numPending),
                getSlotPosition(numPending, numPending),
                animationProgress
              )}px`,
              top: 0,
              zIndex: 10,
            }}
          />
        )}

        {/* Vertical divider - orange normally, red → orange during animation */}
        <div
          style={{
            position: "absolute",
            left: `${dividerX}px`,
            top: "50%",
            transform: "translateY(-50%)",
            width: `${DIVIDER_WIDTH}px`,
            height: "160px",
            backgroundColor: rightSideColor,
            boxShadow: `0 0 20px ${
              isPreAnimating
                ? getRedPillColor(1 - preAnimationProgress).glow
                : isAnimating || isHoldingText || isReverseDecrypting
                ? getRedPillColor(0).glow // Stay red during block movement and reverse decryption
                : isPostAnimating
                ? getRedPillColor(postAnimationProgress).glow
                : "rgba(247, 147, 26, 0.6)"
            }`,
            borderRadius: "2px",
            transition:
              "background-color 0.1s ease-out, box-shadow 0.1s ease-out",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(to bottom, transparent, ${rightSideColor}, transparent)`,
              filter: "blur(4px)",
              transition: "background 0.1s ease-out",
            }}
          />
        </div>

        {/* Confirmed blocks (slots numPending to totalSlots-1) */}
        {/* During animation: red pill effect (red → orange) */}
        {confirmedBlocks.map((block, idx) => {
          const slotIndex = numPending + idx;
          const isLastConfirmed = idx === numConfirmed - 1;

          // Calculate position
          // During animation: all confirmed blocks shift right by one slot
          // The transitioning block takes the first confirmed slot (numPending)
          let xPos: number;
          let opacity = 1;

          if (isAnimating) {
            // All confirmed blocks shift from current slot to next slot
            const fromSlot = slotIndex;
            const toSlot = slotIndex + 1;
            const fromX = getSlotPosition(fromSlot, numPending);
            const toX = getSlotPosition(toSlot, numPending);
            xPos = lerp(fromX, toX, animationProgress);

            // Last block fades out as it moves off-screen
            if (isLastConfirmed) {
              opacity = 1 - animationProgress;
            }
          } else {
            xPos = getSlotPosition(slotIndex, numPending);
          }

          // Calculate red pill progress for confirmed blocks
          // Pre-animation: orange → red (progress goes from 1 to 0)
          // Main animation + Hold + Reverse: stay RED (progress = 0)
          // Post-animation: red → orange (progress goes from 0 to 1)
          let blockRedPillProgress: number | undefined;
          if (isPreAnimating) {
            blockRedPillProgress = 1 - preAnimationProgress; // orange → red
          } else if (isAnimating || isHoldingText || isReverseDecrypting) {
            blockRedPillProgress = 0; // stay red during block movement and reverse decryption
          } else if (isPostAnimating) {
            blockRedPillProgress = postAnimationProgress; // red → orange
          }

          return (
            <BlockCard
              key={`confirmed-${block.height}`}
              height={block.height}
              txCount={block.txCount}
              medianFee={block.medianFee}
              redPillProgress={blockRedPillProgress}
              totalFees={block.totalFees}
              style={{
                left: `${xPos}px`,
                top: 0,
                opacity,
              }}
            />
          );
        })}
      </div>

      {/* Test button for Matrix "red pill" animation */}
      {showTestButton && (
        <button
          onClick={triggerTestAnimation}
          disabled={isPreAnimating || isAnimating || isPostAnimating || isHoldingText || isReverseDecrypting}
          style={{
            marginTop: "24px",
            padding: "8px 16px",
            borderRadius: "4px",
            fontSize: "0.875rem",
            backgroundColor:
              isPreAnimating || isAnimating || isPostAnimating || isHoldingText || isReverseDecrypting
                ? "rgba(247, 147, 26, 0.2)"
                : "rgba(247, 147, 26, 0.4)",
            color: "#b7ab98",
            border: "1px solid rgba(247, 147, 26, 0.5)",
            cursor:
              isPreAnimating || isAnimating || isPostAnimating || isHoldingText || isReverseDecrypting
                ? "not-allowed"
                : "pointer",
          }}
        >
          {isPreAnimating
            ? "Erwacht..."
            : isAnimating
            ? "Block bewegt sich..."
            : isPostAnimating
            ? "Wird orange..."
            : isHoldingText
            ? "MATRIX / WAHRHEIT..."
            : isReverseDecrypting
            ? "Zurück zur Realität..."
            : "Block-Animation testen"}
        </button>
      )}
    </div>
  );
}
